// src/components/SmartRemindersPanel.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
  Button,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import {
  Warning,
  Schedule,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  NotificationsActive
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import { SmartReminderService } from '../services/smartReminderService';

const SmartRemindersPanel = ({ user }) => {
  const { t } = useI18n();
  const [reminderService] = useState(new SmartReminderService());
  const [reminders, setReminders] = useState([]);
  const [overdueReminders, setOverdueReminders] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReminders();
    // Генеруємо нові нагадування при завантаженні
    reminderService.generateSmartReminders().then(() => loadReminders());
  }, []);

  const loadReminders = () => {
    const active = reminderService.getActiveReminders();
    const overdue = reminderService.getOverdueReminders();
    setReminders(active);
    setOverdueReminders(overdue);
    setLoading(false);
  };

  const handleMarkCompleted = async (reminderId) => {
    await reminderService.markAsCompleted(reminderId);
    loadReminders();
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <Warning sx={{ color: 'error.main' }} />;
      case 'medium': return <Schedule sx={{ color: 'warning.main' }} />;
      default: return <Schedule sx={{ color: 'info.main' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>{t('common_loading')}</Typography>
        </CardContent>
      </Card>
    );
  }

  const totalReminders = reminders.length + overdueReminders.length;

  return (
    <Card>
      <CardContent sx={{ p: 2 }}>
        {/* Заголовок */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <NotificationsActive color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('smart_reminders')}
          </Typography>
          <Chip 
            label={totalReminders} 
            color={overdueReminders.length > 0 ? 'error' : 'primary'}
            size="small"
          />
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          {/* Прострочені нагадування */}
          {overdueReminders.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('overdue_reminders')} ({overdueReminders.length})
              </Typography>
              <List dense>
                {overdueReminders.map(reminder => (
                  <ListItem key={reminder.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {getPriorityIcon(reminder.priority)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {reminder.title}
                          </Typography>
                          <Chip
                            label={t('overdue')}
                            color="error"
                            size="small"
                          />
                        </Box>
                      }
                      secondary={reminder.message}
                    />
                    <Button
                      size="small"
                      onClick={() => handleMarkCompleted(reminder.id)}
                      startIcon={<CheckCircle />}
                    >
                      {t('mark_done')}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* Активні нагадування */}
          {reminders.length > 0 ? (
            <List dense>
              {reminders.map(reminder => (
                <ListItem key={reminder.id} sx={{ px: 0 }}>
                  <ListItemIcon>
                    {getPriorityIcon(reminder.priority)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {reminder.title}
                        </Typography>
                        <Chip
                          label={t(`priority_${reminder.priority}`)}
                          color={getPriorityColor(reminder.priority)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {reminder.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('due')}: {new Date(reminder.dueDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <Button
                    size="small"
                    onClick={() => handleMarkCompleted(reminder.id)}
                    startIcon={<CheckCircle />}
                  >
                    {t('mark_done')}
                  </Button>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
              {t('no_active_reminders')}
            </Typography>
          )}

          {/* Кнопки управління */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => reminderService.generateSmartReminders().then(() => loadReminders())}
            >
              {t('generate_reminders')}
            </Button>
            <Button
              size="small"
              onClick={() => reminderService.cleanupOldReminders().then(() => loadReminders())}
            >
              {t('cleanup_old')}
            </Button>
          </Stack>
        </Collapse>

        {!expanded && totalReminders > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('reminders_collapsed', { count: totalReminders })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartRemindersPanel;